import {
  ErrorList,
  ErrorListContext,
  getErrorDisplayInfo,
  getGraphQLErrorCode,
  GraphQLError
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';

export type ErrorStore = {
  readonly addError: (error: GraphQLError) => void;
};

export type RegisterErrorStoreInHostProps = {
  readonly registerErrorStore: (errorStore: ErrorStore) => void;
  readonly unregisterErrorStore: () => void;
};

/**
 * This component allows the error list (provided via the React context) to be "registered" in external host applications
 * (outside DC3, e.g. VizPortal Client), so they can report errors into the ErrorList and have them be displayed
 * via our infrastructure.
 *
 * When this component mounts, it calls the `registerErrorStore` function with the error store from the current context.
 * The host app can then save that store in a place where it can send errors to it over time.
 *
 * When this component unmounts, in calls `unregisterErrorStore`, which signals to the host that the error store is
 * no longer valid, and that the host should stop sending errors to it.
 *
 * Errors reported to the store from the host will be added to the error list in the most immediate applicable context.
 * This component will not display the errors; that is handled separately via the `ErrorToasts` component.
 */
export const RegisterErrorStoreInHost: React.ComponentType<RegisterErrorStoreInHostProps> = withRouter(RegisterErrorStoreInHostComponent);

export function RegisterErrorStoreInHostComponent(props: RegisterErrorStoreInHostProps & RouteComponentProps): null {
  const errorList = React.useContext(ErrorListContext);
  // This unbound method isn't a problem – our ErrorList doesn't use `this`.
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const memoizedErrorStore = React.useMemo(() => makeErrorStore(errorList.addError), [errorList.addError]);

  // Register/unregister on mount/unmount
  React.useEffect(
    () => {
      props.registerErrorStore(memoizedErrorStore);
      return () => props.unregisterErrorStore();
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it!
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.registerErrorStore, props.unregisterErrorStore, memoizedErrorStore]
  );

  // Clear the error store when the URL changes, which corresponds to the rowStore/query changing
  React.useEffect(
    () => {
      errorList.clearErrors();
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it!
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.location]
  );

  return null;
}

function makeErrorStore(addError: ErrorList['addError']): ErrorStore {
  return {
    addError: (error) => addError(getErrorDisplayInfo(getGraphQLErrorCode(error))),
  };
}
